import { api } from '@/lib/axios'
import { downloadFile, extractFilename } from '@/lib/download'
import { mitStand } from '@/lib/speichern'
import type {
  HauptwerkstoffErgebnis,
  DeclarationCheckItem,
  MaterialCertificateType,
  ProjectDeclarationStatus,
  ResolvedTechField,
  TechField,
  TechSchema,
  TechValues,
} from '@symphenia/shared-types'

/**
 * AP-35 Phase F — Technik-Blatt, Leistungserklärung, Materialzertifikate
 * (Detailblatt 6, Detailblatt 5 7.).
 */

export interface TechTemplate {
  id: string
  name: string
  description: string
  fieldCount: number
}

export interface TechSchemaState {
  schema: TechSchema
  inUse: Record<string, number>
}

export interface PositionTech {
  schema: TechSchema
  fields: Record<string, ResolvedTechField>
  effective: TechValues
  /** AP-36 8.1: Stand der Position — in ihr stehen die Technikwerte, ihr `updatedAt` ist deren Stand. */
  updatedAt?: string
}

/** Die Technik-Vorgabe am Projekt (AP-35 Blatt 6 2.6) — die Werte flach, ohne Vererbung. */
export interface ProjectTech {
  schema: TechSchema
  values: TechValues
  /** AP-36 8.1: Stand des Projekts — in ihm stehen die Technikwerte. */
  updatedAt?: string
}

export interface DeclarationVersion {
  id: string
  number: string
  version: number
  status: ProjectDeclarationStatus
  language: 'DE' | 'EN'
  issuedAt: string
  issuedById: string
  issuedByName: string | null
  withdrawnAt: string | null
  withdrawReason: string | null
  contentHash: string
  pdfFileId: string | null
  pdfHash: string | null
  ceLabelFileId: string | null
}

export interface DeclarationState {
  versions: DeclarationVersion[]
  deviates: boolean
  check: DeclarationCheckItem[]
}

export interface ProjectDeclarationRow {
  id: string
  number: string
  version: number
  status: ProjectDeclarationStatus
  issuedAt: string
  pdfFileId: string | null
  ceLabelFileId: string | null
  position: { id: string; number: number; name: string }
}

export interface MaterialCertificate {
  id: string
  type: MaterialCertificateType
  number: string
  issuedAt: string
  issuerClientId: string | null
  issuerName: string | null
  issuer: string | null
  heatNumber: string | null
  material: string | null
  dimension: string | null
  standard: string | null
  note: string | null
  isActive: boolean
  contentHash: string
  /** AP-36 8.6: Stand des Zeugnisses — die Maske schreibt damit feldweise gegen Überschreiben. */
  updatedAt?: string
  file: { id: string; originalName: string; mimeType: string; sizeBytes: number }
  assignments: { lineId: string; projectId: string; projectNumber: string; positionId: string; positionNumber: number }[]
}

// ── Dateien öffnen ─────────────────────────────────────────────────────────

/**
 * Eine Datei, die das Backend nur mit Anmeldung ausliefert. Ein schlichter
 * Link reicht dafür nicht: Das Zugriffstoken steht im Speicher, nicht in einem
 * Cookie — der Browser schickte den Aufruf ohne und bekäme 401.
 */
export async function oeffneGeschuetzt(pfad: string, name: string): Promise<void> {
  const res = await api.get<ArrayBuffer>(pfad, { responseType: 'arraybuffer' })
  const mime = String(res.headers['content-type'] ?? 'application/octet-stream').split(';')[0]
  let datei = extractFilename(res.headers['content-disposition'], name)
  try {
    datei = decodeURIComponent(datei)
  } catch {
    // Name ohne Kodierung — so lassen
  }
  downloadFile(res.data, datei, mime)
}

export const oeffneProjektdatei = (fileId: string, name: string) =>
  oeffneGeschuetzt(`/projects/files/${fileId}/content`, name)

// ── Technik ────────────────────────────────────────────────────────────────

export async function fetchTechTemplates(): Promise<TechTemplate[]> {
  return (await api.get<{ data: TechTemplate[] }>('/projects/tech-templates')).data.data
}

export async function fetchTechSchema(): Promise<TechSchemaState> {
  return (await api.get<{ data: TechSchemaState }>('/projects/settings/tech-schema')).data.data
}

export async function saveTechSchema(fields: TechField[]): Promise<TechSchemaState> {
  return (await api.put<{ data: TechSchemaState }>('/projects/settings/tech-schema', { fields })).data.data
}

export async function applyTechTemplate(templateId: string): Promise<TechSchemaState> {
  return (await api.post<{ data: TechSchemaState }>(`/projects/settings/tech-schema/apply/${templateId}`)).data.data
}

export async function fetchProjectTech(projectId: string): Promise<ProjectTech> {
  return (await api.get<{ data: ProjectTech }>(`/projects/${projectId}/tech`)).data.data
}

/** `stand` ist das `updatedAt`, das die Maske kennt (AP-36 8.1) — ohne ihn schreibt der Aufruf wie bisher. */
export async function saveProjectTech(projectId: string, values: TechValues, stand?: string): Promise<ProjectTech> {
  return (await api.put<{ data: ProjectTech }>(`/projects/${projectId}/tech`, { values }, mitStand(stand))).data.data
}

export async function fetchPositionTech(projectId: string, positionId: string): Promise<PositionTech> {
  return (await api.get<{ data: PositionTech }>(`/projects/${projectId}/positions/${positionId}/tech`)).data.data
}

export async function savePositionTech(projectId: string, positionId: string, values: TechValues, stand?: string): Promise<PositionTech> {
  return (await api.put<{ data: PositionTech }>(`/projects/${projectId}/positions/${positionId}/tech`, { values }, mitStand(stand))).data.data
}

// ── Leistungserklärung ─────────────────────────────────────────────────────

/** Nachtrag 4 — Hauptwerkstoff der Stückliste; schreibt nichts. */
export async function fetchMainMaterial(projectId: string, positionId: string): Promise<HauptwerkstoffErgebnis> {
  return (await api.get<{ data: HauptwerkstoffErgebnis }>(`/projects/${projectId}/positions/${positionId}/tech/main-material`)).data.data
}

export async function fetchDeclarations(projectId: string, positionId: string): Promise<DeclarationState> {
  return (await api.get<{ data: DeclarationState }>(`/projects/${projectId}/positions/${positionId}/declarations`)).data.data
}

export async function issueDeclaration(projectId: string, positionId: string, language?: 'DE' | 'EN'): Promise<DeclarationState> {
  return (
    await api.post<{ data: DeclarationState }>(`/projects/${projectId}/positions/${positionId}/declarations`, language ? { language } : {})
  ).data.data
}

export async function previewDeclaration(projectId: string, positionId: string, language?: 'DE' | 'EN') {
  const res = await api.post<ArrayBuffer>(
    `/projects/${projectId}/positions/${positionId}/declarations/preview${language ? `?language=${language}` : ''}`,
    {},
    { responseType: 'arraybuffer' },
  )
  downloadFile(res.data, 'Leistungserklaerung-Vorschau.pdf', 'application/pdf')
}

export async function withdrawDeclaration(projectId: string, declarationId: string, reason: string): Promise<DeclarationState> {
  return (await api.post<{ data: DeclarationState }>(`/projects/${projectId}/declarations/${declarationId}/withdraw`, { reason })).data.data
}

export async function renderDeclaration(projectId: string, declarationId: string): Promise<DeclarationState> {
  return (await api.post<{ data: DeclarationState }>(`/projects/${projectId}/declarations/${declarationId}/render`)).data.data
}

export async function fetchProjectDeclarations(projectId: string): Promise<ProjectDeclarationRow[]> {
  return (await api.get<{ data: ProjectDeclarationRow[] }>(`/projects/${projectId}/declarations`)).data.data
}

// ── Materialzertifikate ────────────────────────────────────────────────────

export interface CertificateFilter {
  q?: string
  type?: string
  from?: string
  to?: string
  includeInactive?: boolean
  unassigned?: boolean
}

export async function fetchCertificates(filter: CertificateFilter): Promise<{ data: MaterialCertificate[]; total: number }> {
  const res = await api.get<{ data: MaterialCertificate[]; meta: { total: number } }>('/material-certificates', { params: filter })
  return { data: res.data.data, total: res.data.meta.total }
}

export async function createCertificate(file: File, felder: Record<string, string | undefined>) {
  const form = new FormData()
  form.append('file', file)
  for (const [k, v] of Object.entries(felder)) if (v) form.append(k, v)
  return (await api.post<{ data: MaterialCertificate }>('/material-certificates', form)).data.data
}

export async function updateCertificate(id: string, daten: Record<string, unknown>) {
  return (await api.patch<{ data: MaterialCertificate }>(`/material-certificates/${id}`, daten)).data.data
}

export async function deleteCertificate(id: string) {
  await api.delete(`/material-certificates/${id}`)
}

export const oeffneZeugnis = (id: string, name: string) => oeffneGeschuetzt(`/material-certificates/${id}/content`, name)
